import React, { useState, useEffect } from 'react';
import { documentsAPI } from '../../utils/api';
import './Documents.css';

const Documents = () => {
  const [documents, setDocuments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState('');

  useEffect(() => {
    // Fetch documents from API
    const fetchDocuments = async () => {
      try {
        const response = await documentsAPI.getAll();
        setDocuments(response.documents || []);
      } catch (error) {
        console.error('Error fetching documents:', error);
      }
    };
    fetchDocuments();
  }, []);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Backend only accepts PDF files
    if (!file.name.endsWith('.pdf')) {
      setUploadMessage('Only PDF files are allowed');
      setTimeout(() => setUploadMessage(''), 3000);
      return;
    }

    setIsUploading(true);
    setUploadMessage('');

    try {
      await documentsAPI.upload(file);
      
      setUploadMessage('Document uploaded and processed successfully!');
      
      // Refresh documents list
      const updatedDocuments = await documentsAPI.getAll();
      setDocuments(updatedDocuments.documents || []);
      
      setTimeout(() => setUploadMessage(''), 3000);
    } catch (error) {
      setUploadMessage(error.response?.data?.detail || 'Error uploading document');
      setTimeout(() => setUploadMessage(''), 5000);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteDocument = async (documentId, filename) => {
    // Confirm deletion
    if (!window.confirm(`Are you sure you want to delete "${filename}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await documentsAPI.delete(documentId);
      
      setUploadMessage('Document deleted successfully!');
      
      // Refresh documents list
      const updatedDocuments = await documentsAPI.getAll();
      setDocuments(updatedDocuments.documents || []);
      
      setTimeout(() => setUploadMessage(''), 3000);
    } catch (error) {
      setUploadMessage(error.response?.data?.detail || 'Error deleting document');
      setTimeout(() => setUploadMessage(''), 5000);
    }
  };

  return (
    <div className="documents-page">
      <div className="page-header">
        <h2>Documents</h2>
        <label className="btn btn-primary">
          <input
            type="file"
            accept=".pdf,.docx,.txt"
            onChange={handleFileUpload}
            style={{ display: 'none' }}
            disabled={isUploading}
          />
          {isUploading ? 'Uploading...' : '+ Upload Document'}
        </label>
      </div>

      {uploadMessage && (
        <div className={`message ${uploadMessage.includes('Error') ? 'error' : 'success'}`}>
          {uploadMessage}
        </div>
      )}

      <div className="documents-info">
        <div className="info-card">
          <div className="info-number">{documents.length}</div>
          <div className="info-label">Total Documents</div>
        </div>
        <div className="info-card">
          <div className="info-number">{documents.filter(d => d.status === 'processed').length}</div>
          <div className="info-label">Processed</div>
        </div>
        <div className="info-card">
          <div className="info-number">50</div>
          <div className="info-label">Max Pages/Doc</div>
        </div>
      </div>

      <div className="documents-list">
        {documents.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📄</div>
            <h3>No documents yet</h3>
            <p>Upload your first document to get started</p>
          </div>
        ) : (
          documents.map((doc) => {
            // Format date
            const uploadDate = doc.created_at 
              ? new Date(doc.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'
                })
              : 'Unknown date';
            
            return (
              <div key={doc.id} className="document-card">
                <div className="document-icon">📄</div>
                <div className="document-info">
                  <h4>{doc.filename || doc.name || 'Untitled Document'}</h4>
                  <p>Uploaded on {uploadDate}</p>
                </div>
                <div className="document-actions">
                  <div className="document-status">
                    <span className={`status-badge ${doc.status || 'processing'}`}>
                      {doc.status || 'processing'}
                    </span>
                  </div>
                  <button
                    className="delete-btn"
                    onClick={() => handleDeleteDocument(doc.id, doc.filename || doc.name || 'Untitled Document')}
                    title="Delete document"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Documents;
